import { useCallback, useState } from "react";

const resolveErrorMessage = (error) =>
  error?.message || "Unable to load preview data.";

export const usePreview = ({
  loadPreviewData,
  buildPreviewContent,
  getTitle,
  getDescription,
} = {}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewSource, setPreviewSource] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewLoading(false);
    setPreviewError("");
    setPreviewSource(null);
    setPreviewData(null);
    setPreviewContent(null);
  }, []);

  const onPreviewOpenChange = useCallback(
    (open) => {
      if (!open) {
        closePreview();
        return;
      }
      setPreviewOpen(true);
    },
    [closePreview],
  );

  const openPreview = useCallback(
    async (source) => {
      setPreviewOpen(true);
      setPreviewLoading(true);
      setPreviewError("");
      setPreviewSource(source);
      setPreviewData(null);
      setPreviewContent(null);

      try {
        const data = (await loadPreviewData?.(source)) ?? null;
        const content = buildPreviewContent
          ? buildPreviewContent({ source, data })
          : null;

        setPreviewData(data);
        setPreviewContent({
          title:
            content?.title || getTitle?.(source) || source?.name || "Preview",
          description:
            content?.description ||
            getDescription?.(source) ||
            "Review the current preview content.",
          ...content,
        });

        return data;
      } catch (error) {
        setPreviewError(resolveErrorMessage(error));
        return null;
      } finally {
        setPreviewLoading(false);
      }
    },
    [buildPreviewContent, getDescription, getTitle, loadPreviewData],
  );

  return {
    previewOpen,
    previewLoading,
    previewError,
    previewSource,
    previewData,
    previewContent,
    openPreview,
    closePreview,
    onPreviewOpenChange,
    setPreviewContent,
  };
};

export default usePreview;
